import {
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Progress,
  Stack,
  Text,
  VStack,
  useClipboard,
  useToast,
} from "@chakra-ui/react";
import { FaCloudUploadAlt, FaWarehouse } from "react-icons/fa";
import ReactS3Uploader from "react-s3-uploader";
import VenueDisplayHeader from "../../VenueDisplayHeader";
import { FiCopy, FiEdit2, FiExternalLink } from "react-icons/fi";
import { EventDetailSummarySection } from "@common/interfaces/KYDEvent";
import { useLoading, useProgress } from "../../hooks/form";
import { useCallback, useContext, useRef } from "react";
import actions from "../../../actions";
import { useDialog } from "../../../Common/Dialog";
import { EditModalType, showSuccessToast } from "../../../utils";
import EditVenueModal from "../Venue/EditVenueModal";
import { SectionProps } from "../sections/sectionsComponents";
import EditNameModal from "./EditNameModal";
import ShowIfHasPermission from "../../ShowIfHasPermission";
import { IoEllipsisVertical } from "react-icons/io5";
import DuplicateEventModal from "./DuplicateEventModal";
import { useNavigate } from "react-router-dom";
import { ScheduledUpdatesBadge } from "../Lineup/ScheduledUpdatesBadge";
import useScheduledUpdates from "../ScheduledUpdates/hooks/useScheduledUpdates";
import {
  ScheduledUpdatesContext,
  withScheduledUpdates,
} from "../ScheduledUpdates/ScheduledUpdatesContext";
import UpcomingScheduledUpdateDialog, {
  UpcomingScheduledUpdateDialogContext,
  withUpcomingScheduledUpdateDialog,
} from "../ScheduledUpdates/UpcomingScheduledUpdateDialog";
import EventDetailTitle from "../../EventDetailTitle";

const EventSummarySection = ({
  data,
  isLoading,
  setModalType,
  permissions = [],
  modalType,
  onUpdateEvent,
}: SectionProps<EventDetailSummarySection["data"]>) => {
  const navigate = useNavigate();
  const { onCopy } = useClipboard(data?.preview_url);
  const { onCopy: _onCopyId } = useClipboard(data?.id);
  const {
    state: { hasScheduledUpdatesForField, getScheduledUpdateForField },
  } = useScheduledUpdates();
  const uploadRef = useRef<HTMLInputElement>(null);
  const [progress, updateProgress] = useProgress({
    poster: false,
  });
  const dialog = useDialog();
  const toast = useToast();
  const [loading, updateLoading] = useLoading({
    event: false,
    blocking: false,
    duplicate: false,
  });
  const hasVenue = !!data.venue;

  const onCopyId = useCallback(() => {
    _onCopyId();
    toast({
      title: "Event ID copied to clipboard",
      status: "success",
      duration: 1000,
      isClosable: true,
    });
  }, [toast]);

  const onDuplicate = useCallback(
    ({ start_at }: { start_at: string }) => {
      return actions.ADMIN.duplicateEvent(data.id, { start_at });
    },
    [data]
  );
  const { open: openScheduledUpdates } = useContext(ScheduledUpdatesContext);
  const {
    isOpen: isUpcomingScheduledUpdateDialogOpen,
    open: openUpcomingScheduledUpdateDialog,
    close: closeUpcomingScheduledUpdateDialog,
  } = useContext(UpcomingScheduledUpdateDialogContext);

  return (
    <>
      <UpcomingScheduledUpdateDialog
        isLoading={isLoading}
        onEditScheduledUpdate={() => {
          console.log("onEditScheduledUpdate");
          const scheduledUpdate = getScheduledUpdateForField(
            data.scheduled_updates,
            "name"
          );

          closeUpcomingScheduledUpdateDialog();
          if (scheduledUpdate) {
            openScheduledUpdates({ scheduledUpdate, field: "name" });
          }
        }}
      />
      <EditVenueModal
        isLoading={loading.event || isLoading}
        isOpen={modalType === EditModalType.VENUE}
        data={data}
        onClose={() => setModalType(EditModalType.INACTIVE)}
        onSave={async (value) => {
          try {
            await onUpdateEvent("venue", JSON.stringify(value));
          } finally {
            updateLoading("event", false);
          }
        }}
      />
      <ShowIfHasPermission permissions={permissions} checkFor="write">
        <DuplicateEventModal
          data={{
            name: data.name,
            start_at: data.start_at,
            timezone: data.timezone,
          }}
          isOpen={modalType === EditModalType.DUPLICATE}
          isLoading={loading.event || loading.duplicate}
          onClose={() => setModalType(EditModalType.INACTIVE)}
          onSave={async (value) => {
            updateLoading("duplicate", true);

            try {
              const duplicated = await onDuplicate(value);

              toast({
                title: `${data.name} - event duplicated`,
                description: `You're now seeing "[Copy] ${data.name}" which now in DRAFT state.`,
                status: "success",
              });

              navigate(`/events/${duplicated.event_id}`);
            } catch (e) {
              console.log(e);
              toast({
                title: "Error duplicating event",
                description: `Was not possible to duplicate event, please contact support`,
                status: "error",
              });
            } finally {
              setModalType(EditModalType.INACTIVE);
              updateLoading("duplicate", false);
            }
          }}
        />
      </ShowIfHasPermission>
      <EditNameModal
        data={data}
        isOpen={modalType === EditModalType.NAME}
        isLoading={loading.event || isLoading}
        onClose={() => setModalType(EditModalType.INACTIVE)}
        onSave={async (value) => {
          try {
            if ("name" in value) {
              await onUpdateEvent("name", value.name);
            }

            if ("subtitle" in value) {
              await onUpdateEvent("subtitle", value.subtitle);
            }

            if ("age_restriction" in value) {
              await onUpdateEvent("age_restriction", value.age_restriction);
            }

            const scheduledUpdate = getScheduledUpdateForField(
              data.scheduled_updates,
              "name"
            );

            if (scheduledUpdate) {
              openUpcomingScheduledUpdateDialog();
            }
          } finally {
            updateLoading("event", false);
          }
        }}
      />
      <Stack direction={["column", "row"]}>
        <Stack w="100%" direction={{ base: "column", md: "row" }} spacing={3}>
          <VStack>
            <Image
              fit={"contain"}
              rounded={"lg"}
              maxH="150px"
              maxW={"150px"}
              src={data.image}
              fallbackSrc="https://content.kydlabs.com/system/plcaeholderimage.png"
            />
            <ShowIfHasPermission permissions={permissions} checkFor="write">
              <Button
                isDisabled={isLoading || loading.event}
                isLoading={isLoading || loading.event}
                onClick={() => {
                  if (uploadRef && uploadRef.current) {
                    uploadRef.current.click();
                  }
                }}
                aria-label="Upload event image"
                variant={"outline"}
                leftIcon={<FaCloudUploadAlt />}
              >
                Change Poster
              </Button>
            </ShowIfHasPermission>
            <ShowIfHasPermission permissions={permissions} checkFor="write">
              <>
                {progress.poster > 0 && (
                  <Progress
                    h="15px"
                    rounded={"lg"}
                    colorScheme={"green"}
                    value={progress.poster}
                  />
                )}
                {!progress.poster || progress.poster === 0 ? (
                  <Box h="15px" w="100%" />
                ) : null}
                <ReactS3Uploader
                  style={{ display: "none" }}
                  //@ts-ignore
                  inputRef={uploadRef}
                  getSignedUrl={(file, callback) =>
                    actions.ADMIN.createUploadUrl(file, callback)
                  }
                  accept="image/*"
                  onProgress={(p) => {
                    updateProgress("poster", p);
                  }}
                  onError={(e) => {
                    updateLoading("event", false);
                    updateProgress("poster", 0);
                    dialog({ text: e });
                  }}
                  preprocess={(f, next) => {
                    updateLoading("event", true);
                    next(f);
                  }}
                  onFinish={async (result) => {
                    try {
                      await onUpdateEvent("poster", result.fileKey);
                    } finally {
                      updateProgress("poster", 0);
                      updateLoading("event", false);
                    }
                  }}
                  uploadRequestHeaders={(file) => ({
                    "Content-Type": file.type,
                    "x-amz-acl": "private",
                  })}
                  contentDisposition="auto"
                />
              </>
            </ShowIfHasPermission>
          </VStack>
          <Stack w="100%" spacing={4}>
            <Stack
              w="100%"
              justify={"space-between"}
              direction={{ base: "column", md: "row" }}
            >
              <Stack spacing={0} justifyContent={"center"}>
                <HStack
                  justifyContent={"space-between"}
                  alignItems={"flex-start"}
                >
                  <Stack
                    direction={["column", "row"]}
                    alignItems={["flex-start", "center"]}
                    alignSelf={"flex-start"}
                    gap={2}
                    pb={[2, 0]}
                  >
                    <Stack spacing={0}>
                      <EventDetailTitle>
                        <Text
                          w="full"
                          fontSize={"xl"}
                          onClick={onCopyId}
                          cursor="pointer"
                        >
                          {data.name}
                        </Text>
                      </EventDetailTitle>
                      {data.subtitle ? (
                        <Text w="full">{data.subtitle}</Text>
                      ) : null}
                    </Stack>

                    <ScheduledUpdatesBadge
                      scheduledUpdates={data.scheduled_updates}
                      field="name"
                      hasScheduledUpdatesForField={hasScheduledUpdatesForField}
                    />
                  </Stack>
                  <ShowIfHasPermission
                    permissions={permissions}
                    checkFor="write"
                  >
                    <IconButton
                      isDisabled={isLoading || loading["blocking"]}
                      onClick={() => setModalType(EditModalType.NAME)}
                      icon={<FiEdit2 fontSize="1.25rem" />}
                      variant="ghost"
                      aria-label="Edit member"
                    />
                  </ShowIfHasPermission>
                </HStack>
                {data.subtitle ? <Divider my={2} /> : null}
                <VenueDisplayHeader venue={data.venue} />
                <ShowIfHasPermission permissions={permissions} checkFor="write">
                  <Button
                    maxW={"180px"}
                    isDisabled={isLoading || loading.event}
                    isLoading={loading.event}
                    onClick={() => setModalType(EditModalType.VENUE)}
                    aria-label="Change venue"
                    variant={"outline"}
                    leftIcon={<FaWarehouse />}
                  >
                    {hasVenue ? "Change Venue" : "Set Venue"}
                  </Button>
                </ShowIfHasPermission>
                <Stack spacing={0}>
                  {data.display_start_at ? (
                    <Text fontSize={"sm"}>
                      Event start: {data.display_start_at}
                    </Text>
                  ) : null}
                  {data.display_start_at ? (
                    <Text fontSize={"sm"}>
                      Event end: {data.display_end_at}
                    </Text>
                  ) : null}
                  {data.timezone ? (
                    <Text fontSize={"sm"}>Timezone: {data.timezone}</Text>
                  ) : null}
                  {data.is_external ? (
                    <Badge colorScheme={"blue"}>External</Badge>
                  ) : null}
                </Stack>
              </Stack>

              <Stack>
                {data.preview_url ? (
                  <HStack>
                    <ShowIfHasPermission
                      permissions={permissions}
                      checkFor="write"
                    >
                      <>
                        <Button
                          variant={"outline"}
                          onClick={() =>
                            window.open(data.preview_url, "_blank")
                          }
                          rightIcon={<FiExternalLink />}
                        >
                          Preview Event
                        </Button>
                        <IconButton
                          variant={"outline"}
                          onClick={() => {
                            onCopy();
                            showSuccessToast(toast, "Preview Link Copied");
                          }}
                          icon={<FiCopy />}
                          aria-label="Copy preview link"
                        />
                      </>
                    </ShowIfHasPermission>
                  </HStack>
                ) : null}
                {data.short_link ? (
                  <Button
                    variant={"outline"}
                    onClick={() => window.open(data.short_link, "_blank")}
                    rightIcon={<FiExternalLink />}
                  >
                    Go To Event
                  </Button>
                ) : null}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        <ShowIfHasPermission permissions={permissions} checkFor="write">
          <Menu>
            <MenuButton
              as={IconButton}
              variant={"outline"}
              icon={<IoEllipsisVertical />}
            />
            <MenuList>
              <MenuItem onClick={() => setModalType(EditModalType.DUPLICATE)}>
                Duplicate
              </MenuItem>
            </MenuList>
          </Menu>
        </ShowIfHasPermission>
      </Stack>

      <Divider />
    </>
  );
};

export default withScheduledUpdates(
  withUpcomingScheduledUpdateDialog(EventSummarySection)
);
